import React from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import styles from './EnhancedExperienceNotice.css';

/**
 * Builds the enhanced experience (Omni) providers URL using the same host
 * resolution logic as the OmniBanner announcement.
 */
export const getEnhancedExperienceURL = () => {
  const omniHost = config.omni?.host || 'omni.mulesoft.com';

  return `https://${omniHost}/providers`;
};

/**
 * EnhancedExperienceNotice
 *
 * Inline note shown next to the OAuth2 Authorization Code auth scheme, which is
 * only available in the enhanced (Omni) experience. Renders explanatory text
 * plus a "Switch to enhanced experience" pill that links to the Omni providers
 * page in a new tab.
 */
const EnhancedExperienceNotice = ({ message, testId, actions }) => {
  const handleClick = () => {
    window.open(getEnhancedExperienceURL(), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.notice} data-test-id={testId}>
      <p className={styles.message}>{message}</p>
      {/* Pill and any adjacent actions (e.g. a Close button) are grouped so
          they always sit together on the same line. */}
      <div className={styles['pill-row']}>
        <button
          type="button"
          className={styles.pill}
          onClick={handleClick}
          data-test-id={`${testId}-switch-link`}
        >
          Switch to enhanced experience
          <Icon name="open" size="xs" className={styles.icon} isDecorative />
        </button>
        {actions}
      </div>
    </div>
  );
};

EnhancedExperienceNotice.displayName = 'EnhancedExperienceNotice';

EnhancedExperienceNotice.propTypes = {
  /** Explanatory text shown above the pill */
  message: PropTypes.string,
  /** Test id root for the notice wrapper */
  testId: PropTypes.string,
  /** Optional node(s) rendered inline after the pill (e.g. a Close button) */
  actions: PropTypes.node
};

EnhancedExperienceNotice.defaultProps = {
  message:
    'OAuth2 Authorization Code Flow scanners are only available in the enhanced experience.',
  testId: 'enhanced-experience-notice',
  actions: null
};

export default EnhancedExperienceNotice;
